import React, { useMemo } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';

import { AUTH_TOKEN, getFromLocalStorage } from 'utils/localStorage';
import { getFileExtension, translateToMB } from './helpers';

const { API_URL } = process.env;
const token = getFromLocalStorage(AUTH_TOKEN);
const DEFAULT_MIN_SIZE = 0;
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024;
const PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

interface Props {
  value?: UploadFile | null,
  onChange: (files: UploadFile[]) => Promise<any>;
  maxCount?: number;
  minSize?: number;
  maxSize?: number;
  extensions?: string[];
  name?: string;
}

export default function FileUpload({
  value = null,
  onChange,
  maxCount = 10,
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  extensions = PHOTO_EXTENSIONS,
  name = "file",
}: Props) {
  const normalizeValue = (value: UploadFile) => {
    if (value.status === 'done' && value.response) {
      return {
        id: value.response.id,
        thumbUrl: value.response.filePath,
        ...value,
      };
    }
    return value;
  };

  const handleChange = ({ fileList }: UploadChangeParam) => {
    return onChange(fileList.map(normalizeValue).slice());
  };

  const fileList = useMemo(() => {
    if (value) {
      if (Array.isArray(value)) {
        return value;
      }
      return [value];
    }
    return [];
  }, [value]);

  const beforeUpload = (file: File) => {
    try {
      const fileExtension = getFileExtension(file);
      if (!extensions.includes(fileExtension)) {
        message.error('Invalid extension. Valid: ' + extensions.join(','));
        return Upload.LIST_IGNORE;
      }
      if (file.size > maxSize || file.size < minSize) {
        message.error(`Недопустимый размер: ${translateToMB(file.size)}. Разрешено от ${translateToMB(minSize)} до ${translateToMB(maxSize)}`);
        return Upload.LIST_IGNORE;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <Upload
      action={`${API_URL}/icons`}
      name={name}
      listType="picture-card"
      accept={extensions.join(',')}
      maxCount={maxCount}
      fileList={fileList}
      showUploadList={{ showPreviewIcon: false }}
      onChange={handleChange}
      beforeUpload={beforeUpload}
      headers={{
        Authorization: `Bearer ${token}`,
      }}
    >
      {(!fileList || !(fileList.length >= maxCount)) && (
        <PlusOutlined />
      )}
    </Upload>
  );
}
